import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Primary } from "../../stories/Buttons/Button.stories";
import formatData, { formatDtAge, statusMapping } from "../../utils/utils";
import { useLocation, useNavigate, Navigate } from "react-router";
import DuplicateIdTable from "../../stories/Tables/DuplicateIdTable";
import VitalityDuplicatedTable from "../../stories/Tables/VitalityDuplicatedTable";
import img from "../../assets/images/notification.svg";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import { links } from "../../stories/Forms/fields";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import { useAuth } from "../../components/AuthContext/AuthContext";
import useApis from "../../services/useAPI";
import { searchBookingsUrl, patientSearchApi } from "../../utils/constants";
import { XCircleFill } from "react-bootstrap-icons";
import PageLayout from "../../components/Layout/PageLayout";
import Spinner from "react-bootstrap/Spinner";

/**
 * UserDetails component displays details of a patient record.
 * It includes a search form, a table for displaying modified bookings, and a back button.
 * @param {Object} props - Component props.
 * @param {Array} props.bookings - Modified bookings data.
 * @param {Object} props.user - User data.
 * @returns {JSX.Element|null} - Rendered component or null if user data is not available.
 */

function UserDetails({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const vhColumns = [
    {
      dataField: "bookingId",
      text: "Consultation ID",
    },
    {
      dataField: "first_name",
      text: "First name",
    },
    {
      dataField: "last_name",
      text: "Last name",
    },
    {
      dataField: "dob",
      text: "Date of birth",
    },
    {
      dataField: "email",
      text: "Email address",
    },
    {
      dataField: "status",
      text: "Action",
    },
  ];

  const [data, setData] = useState();

  const {
    callApi,
    resultPost,
    serverError,
    tokenError,
    toggleShowError,
    isLoading,
  } = useApis();

  const isClinicalService = location.state?.serviceType === "clinical";
  const selectedService = localStorage.getItem("selectedService");

  const isVhLogin = auth?.featureFlag && selectedService === "healthCare";
  const url = isVhLogin ? patientSearchApi : searchBookingsUrl;

  const handleSubmit = async (data) => {
    const formattedData = data && formatData(data);

    await callApi(formattedData, url, "POST", {
      Authorization: `Bearer ${localStorage.getItem("access_token") ||
        auth.accessToken}`,
      "Content-Type": "application/json",
    });
  };

  const stages = ["patient-record","patient-details","confirm-details","enter-barcode","appointment-confirmation","clinical-screening","clinical-measurement"];
  
  useEffect(() => {
    if (!stages.includes(sessionStorage.getItem("allowedStage")) && auth?.featureFlag) {
      navigate("/patient-record");
    }
  },[sessionStorage.getItem("allowedStage"), auth?.featureFlag, navigate]);

  useEffect(() => {
    if (serverError || tokenError) {
      auth?.showToast(
        "Error",
        tokenError || serverError,
        "../assets/mhc.png",
        {icon: <XCircleFill />},
      );
    }
  }, [serverError, tokenError]);

  useEffect(() => {
    if (!isClinicalService) {
      if (location.state?.data) {
        handleSubmit(location.state?.data);
      }
    }
  }, []);

  const userData = location.state?.user
    ? {
        ...location.state.user,
        dob: formatDtAge(user?.dob || user?.date_of_birth),
      }
    : null;
  const handleBackClick = () => {
    navigate(-1);
  };

  const groupByUserId = (users = []) => {
    const map = {};

    users.forEach((user) => {
      const userId = user.user_id || user.id;

      if (!map[userId]) {
        map[userId] = {
          ...user,
          user_id: userId,
        };
      }
    });

    return Object.values(map);
  };

  const handleSubmitDuplicateId = async (data) => {
    if (isClinicalService || selectedService === "clinical") {
      const { status, ...cleanData } = data;
      return (
        <Navigate
          state={{
            data: cleanData,
            user: resultPost?.user_info,
          }}
          to={"/patient-record/select-patient"}
        />
      );
    } else {
      const { status, dob, ...dataWithoutStatus } = data;
      setData(dataWithoutStatus);
      await callApi(dataWithoutStatus, url, "POST", {
        Authorization: `Bearer ${localStorage.getItem("access_token") ||
          auth.accessToken}`,
        "Content-Type": "application/json",
      });
    }
  };

  useEffect(() => {
    if (!isClinicalService && !resultPost?.duplicate) {
      if (resultPost?.bookings) {
        navigate("/patient-record/patient-details", {
          state: { resultPost, user: userData, isDublicated: true },
        });
      }
    }
  }, [data, resultPost]);

  /**
   * Flattens a nested object into a single-level object with dot-separated keys.
   */
  const flattenObject = (obj, parentKey = "", res = {}) => {
    for (let key in obj) {
      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        flattenObject(obj[key], parentKey + key + ".", res);
      } else {
        res[parentKey + key] = obj[key];
      }
    }
    return res;
  };

  const modifyDuplicateIdBookings = (users, isClinical = false) => {
    return users.map((user, index) => {
      if (isClinical) {
        return {
          patient_id: user.patient_id,
          id: user?.user_id,
          first_name: user.first_name,
          last_name: user.last_name,
          test_type: user.test_type || "Thrive Panel",
          date_of_birth: user.date_of_birth || "N/A",
          sex: user.sex || "N/A",
          crio_identifier: user.crio_identifier || "N/A",

          status: {
            component: (
              <Primary
                hover={true}
                label="Select"
                size="small"
                variant="primary"
              />
            ),
          },
        };
      } else {
        const bookingStatus = statusMapping[
          user.status?.replace(/-/g, "_")
        ] || {
          label: "Unknown",
          variant: "secondary",
        };
        const hover = !["registered", "created", "activating"].includes(
          user.status?.toLowerCase(),
        );

        return {
          bookingId: user.booking_id,
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          dob: user?.dob,
          status: {
            component: (
              <Primary
                hover={hover}
                label={bookingStatus.label || "Unknown"}
                size="small"
                variant={bookingStatus.variant || "secondary"}
              />
            ),
          },
        };
      }
    });
  };

  const usersData = resultPost?.users || location.state?.user || [];
  const uniqueUsers = groupByUserId(usersData);

  const flattenedBookingsDuplicateId =
    usersData?.map((booking) => booking && flattenObject(booking)) || [];

  const sourceBookings = isClinicalService
    ? uniqueUsers
    : flattenedBookingsDuplicateId;

  const isClinicalMode = isClinicalService || selectedService === "clinical";

  const modifiedBookingsDuplicateId = flattenedBookingsDuplicateId.length
    ? modifyDuplicateIdBookings(sourceBookings, isClinicalMode)
    : [];

  const shouldShowDuplicateTable =
    resultPost?.duplicate ||
    location.state?.user?.length > 1 ||
    (selectedService === "clinical" && location.state?.user?.length > 1);
  
  if (isLoading && isVhLogin) {
    return (
      <LoggedInLayout
        buttonLabel="Logout"
        img={img}
        isActivation={false}
        links={links}
        logo={logo}
        pageTitle={
          isClinicalService || selectedService === "clinical"
            ? "Clinical studies"
            : "Healthcare Services"
        }
        userInitials={auth.userInitials}
        isVhlogin={isVhLogin}
        vitalityLayout={isVhLogin}
      >
        <div className="text-center py-4">
          <Spinner
            animation="border"
            className="my-custom-spinner"
          />
        </div>
      </LoggedInLayout>
    );
  }

  return (
    <LoggedInLayout
      buttonLabel={"Logout"}
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={
        isClinicalService || selectedService === "clinical"
          ? "Clinical studies"
          : "Healthcare Services"
      }
      userInitials={auth.userInitials}
      isVhlogin={isVhLogin}
      vitalityLayout={isVhLogin}
    >
      {isVhLogin ? (
        <PageLayout
          heading={`Search results found (${usersData.length})`}
          subHeading=""
        >
          {serverError && <span className="server-error">{serverError}</span>}
          {shouldShowDuplicateTable && (
            <div className="patient-details-page multiple-user-page">
              <div className="container">
                <div className="row">
                  <div className="col-md-12 px-0">
                    <VitalityDuplicatedTable
                      columns={vhColumns}
                      data={modifiedBookingsDuplicateId}
                      onSubmit={handleSubmitDuplicateId}
                      pagination={true}
                      search={false}
                      sort={false}
                      rowKey="id"
                      statusLabel="View"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </PageLayout>
      ) : (
        <>
          {serverError && <span className="server-error">{serverError}</span>}
          {shouldShowDuplicateTable && (
            <div className="patient-details-page multiple-user-page">
              <div className="container">
                <div className="row">
                  <div className="col-md-12">
                    <h3 className="server-error text-blue mb-3">
                      {isClinicalService || selectedService === "clinical"
                        ? "Multiple patients found - Please select from table below"
                        : "Multiple patients are registered with these details. Please select the appropriate one from the table below."}
                    </h3>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <DuplicateIdTable
                      data={modifiedBookingsDuplicateId}
                      onSubmit={handleSubmitDuplicateId}
                      pagination={true}
                      search={false}
                      sort={false}
                      rowKey="id"
                    />
                    <div className="container p-0">
                      <div className="row">
                        <div className="col-md-12">
                          <Primary
                            className="d-block btn-back btn-rounded"
                            label="Back"
                            onClick={() => handleBackClick()}
                            size="small"
                            variant="outline"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </LoggedInLayout>
  );
}

UserDetails.propTypes = {
  user: PropTypes.shape({
    dob: PropTypes.string,
    date_of_birth: PropTypes.string,
    status: PropTypes.string,
    id: PropTypes.string,
    patient_id: PropTypes.string,
    user_id: PropTypes.any,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    booking_id: PropTypes.string,
    test_type: PropTypes.string,
    sex: PropTypes.string,
    crio_identifier: PropTypes.string,
    registration_stage: PropTypes.string,
  }),
};

export default UserDetails;
